/**
 * Explode a recipe (and sub-recipes) into flat inventory demand.
 * Output quantities are in product inventory base units — ready for FIFO later.
 * Does not call lot/inventory services.
 *
 * Scale: explodeQuantity / recipe.portions (see recipeUsageScale.js).
 * Alias: portionsSold === explodeQuantity (POS sold plates or production formula units).
 */

import { sameProductId } from '../../../utils/productIdUtils';
import {
  normalizeRecipe,
  RECIPE_INGREDIENT_KIND,
  normalizePreparationLoss
} from './recipeModel';
import { toInventoryQuantity } from './unitConversion';
import { getRecipeBatchBase } from './recipeUsageScale';

/**
 * Stock demand factor for preparation loss.
 * net usable = raw * (1 - loss) ⇒ raw = net / (1 - loss)
 * @param {number} loss
 * @returns {number}
 */
export function preparationLossFactor(loss) {
  const L = normalizePreparationLoss(loss);
  if (!(L > 0)) return 1;
  if (L >= 1) return Number.POSITIVE_INFINITY;
  return 1 / (1 - L);
}

/**
 * Yield factor: preparationYield 0.75 ⇒ need 1/0.75 raw for usable output.
 * @param {number} preparationYield
 * @returns {number}
 */
export function preparationYieldFactor(preparationYield) {
  const y = Number(preparationYield);
  if (!Number.isFinite(y) || y <= 0) return 1;
  const fraction = y > 1 ? y / 100 : y;
  if (!(fraction > 0)) return 1;
  return 1 / fraction;
}

/**
 * @param {object} productOrNull
 * @returns {object|null}
 */
function indexProduct(productsById, productId) {
  if (!productId || !productsById) return null;
  if (productsById instanceof Map) {
    return productsById.get(String(productId)) || productsById.get(productId) || null;
  }
  if (typeof productsById === 'function') {
    return productsById(productId) || null;
  }
  return productsById[String(productId)] || productsById[productId] || null;
}

/**
 * @param {unknown[]|Map|Record} recipes
 * @returns {Map<string, object>}
 */
function buildRecipeIndex(recipes) {
  const map = new Map();
  if (recipes instanceof Map) {
    for (const [k, v] of recipes.entries()) {
      const r = normalizeRecipe(v);
      if (r) map.set(String(r.id), r);
      else if (v) map.set(String(k), normalizeRecipe({ ...v, id: k }) || v);
    }
    return map;
  }
  const list = Array.isArray(recipes) ? recipes : Object.values(recipes || {});
  for (const raw of list) {
    const r = normalizeRecipe(raw);
    if (r) map.set(String(r.id), r);
  }
  return map;
}

/**
 * Merge demand rows by productId (sum quantity; keep first meta).
 * @param {Array<object>} rows
 * @returns {Array<object>}
 */
export function mergeDemandByProduct(rows) {
  const map = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row?.productId) continue;
    const qty = Number(row.quantity) || 0;
    if (!(qty > 0)) continue;
    const key = String(row.productId);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, { ...row, productId: key, quantity: qty });
    } else {
      prev.quantity += qty;
    }
  }
  return [...map.values()];
}

/**
 * @param {object} params
 * @param {object} params.recipe — active (or any) recipe document
 * @param {number} [params.portionsSold=1] — alias of explodeQuantity (POS plates or formula units)
 * @param {number} [params.explodeQuantity] — preferred name; falls back to portionsSold
 * @param {unknown[]|Map|Record} [params.recipes] — catalog for sub-recipes
 * @param {object|Map|Function} [params.productsById] — optional product unit/pack info
 * @param {boolean} [params.mergeByProduct=true]
 * @returns {{
 *   success: boolean,
 *   demand?: Array<{ productId: string, quantity: number, recipeId: string, recipeVersion: number, dishId: string }>,
 *   error?: string
 * }}
 */
export function explodeRecipeToStockDemand({
  recipe: rawRecipe,
  portionsSold = 1,
  explodeQuantity = null,
  recipes = [],
  productsById = null,
  mergeByProduct = true
} = {}) {
  const root = normalizeRecipe(rawRecipe);
  if (!root) {
    return { success: false, error: 'Invalid recipe' };
  }
  const sold = Number(
    explodeQuantity != null && explodeQuantity !== '' ? explodeQuantity : portionsSold
  );
  if (!Number.isFinite(sold) || sold <= 0) {
    return { success: false, error: 'explodeQuantity/portionsSold must be > 0' };
  }

  const recipeIndex = buildRecipeIndex(recipes);
  if (!recipeIndex.has(String(root.id))) {
    recipeIndex.set(String(root.id), root);
  }

  const demand = [];
  const visiting = new Set();

  /**
   * @param {object} recipe
   * @param {number} unitsForThisRecipe — BOM-base units of THIS formula to apply
   * @param {string} rootDishId
   * @param {string} rootRecipeId
   * @param {number} rootVersion
   */
  function explodeOne(recipe, unitsForThisRecipe, rootDishId, rootRecipeId, rootVersion) {
    const id = String(recipe.id);
    if (visiting.has(id)) {
      throw new Error(`Circular recipe reference involving ${id}`);
    }
    visiting.add(id);

    const batchBase = getRecipeBatchBase(recipe);
    const portionScale = unitsForThisRecipe / batchBase;
    const yieldFactor = preparationYieldFactor(recipe.preparationYield);

    for (const line of recipe.ingredients || []) {
      const lineLoss = preparationLossFactor(line.preparationLoss);
      const lineScale = portionScale * yieldFactor * lineLoss;

      if (line.kind === RECIPE_INGREDIENT_KIND.SUB_RECIPE) {
        const sub = recipeIndex.get(String(line.subRecipeId));
        if (!sub) {
          throw new Error(`Sub-recipe not found: ${line.subRecipeId}`);
        }
        // line.quantity = full sub-recipe batches needed for parent batch
        const childUnits =
          Number(line.quantity) * lineScale * getRecipeBatchBase(sub);
        explodeOne(sub, childUnits, rootDishId, rootRecipeId, rootVersion);
        continue;
      }

      const product = indexProduct(productsById, line.productId);
      const invQty =
        toInventoryQuantity({
          quantity: Number(line.quantity) * lineScale,
          fromUnit: line.unit,
          productUnit: product?.unit,
          conversionToStock: line.conversionToStock,
          itemsPerBox: product?.itemsPerBox ?? product?.unitsPerPack,
          product
        }) || 0;

      if (!(invQty > 0)) continue;

      demand.push({
        productId: String(line.productId),
        quantity: invQty,
        recipeId: String(rootRecipeId),
        recipeVersion: rootVersion,
        dishId: String(rootDishId),
        fromSubRecipeId:
          id !== String(rootRecipeId) ? id : undefined
      });
    }

    visiting.delete(id);
  }

  try {
    explodeOne(root, sold, root.dishId, root.id, root.version);
  } catch (err) {
    return {
      success: false,
      error: err?.message || String(err)
    };
  }

  const out = mergeByProduct ? mergeDemandByProduct(demand) : demand;
  // Re-attach recipe meta after merge (merge keeps first row meta)
  const demandWithMeta = out.map((row) => ({
    productId: row.productId,
    quantity: row.quantity,
    recipeId: String(root.id),
    recipeVersion: root.version,
    dishId: String(root.dishId)
  }));

  return { success: true, demand: demandWithMeta };
}

/**
 * Explode using active-looking recipe list for a dish sale qty.
 * Caller should pass the active recipe (use resolveActiveRecipe first).
 *
 * @param {object} recipe
 * @param {number} portionsSold
 * @param {object} [options]
 */
export function explodeActiveRecipeForSale(recipe, portionsSold, options = {}) {
  return explodeRecipeToStockDemand({
    recipe,
    portionsSold,
    recipes: options.recipes,
    productsById: options.productsById,
    mergeByProduct: options.mergeByProduct !== false
  });
}

/**
 * Convert flat demand to includedStockItems-like pairs (productId + quantity)
 * for legacy comparison.
 * @param {Array<{productId:string,quantity:number}>} demand
 * @returns {Array<{productId:string,quantity:number}>}
 */
export function demandToIncludedStockItemsShape(demand) {
  return (Array.isArray(demand) ? demand : []).map((d) => ({
    productId: String(d.productId),
    quantity: Number(d.quantity)
  }));
}

/**
 * Scale legacy includedStockItems by sold quantity (same as dishInclude × lineQty).
 * @param {Array<{productId:string,quantity:number}>} included
 * @param {number} soldQty
 */
export function scaleIncludedStockItems(included, soldQty) {
  const q = Number(soldQty) || 0;
  if (!(q > 0)) return [];
  const map = new Map();
  for (const row of Array.isArray(included) ? included : []) {
    const productId = String(row?.productId || '').trim();
    const per = Number(row?.quantity);
    if (!productId || !(per > 0)) continue;
    map.set(productId, (map.get(productId) || 0) + per * q);
  }
  return [...map.entries()].map(([productId, quantity]) => ({ productId, quantity }));
}

/**
 * Compare two demand lists for equality (productId + quantity, float tolerance).
 * @param {Array} a
 * @param {Array} b
 * @param {number} [eps=1e-9]
 */
export function demandListsEqual(a, b, eps = 1e-9) {
  const norm = (list) =>
    mergeDemandByProduct(
      (list || []).map((r) => ({
        productId: String(r.productId),
        quantity: Number(r.quantity)
      }))
    ).sort((x, y) => String(x.productId).localeCompare(String(y.productId)));

  const na = norm(a);
  const nb = norm(b);
  if (na.length !== nb.length) return false;
  for (let i = 0; i < na.length; i += 1) {
    if (!sameProductId(na[i].productId, nb[i].productId)) return false;
    if (Math.abs(na[i].quantity - nb[i].quantity) > eps) return false;
  }
  return true;
}
